
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslations } from "@/hooks/useTranslations";

interface StudentRequestDialogProps {
  profile: any;
}

export const StudentRequestDialog = ({ profile }: StudentRequestDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { t } = useTranslations("publicProfile");
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    goals: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await supabase.from('student_requests').insert({
        trainer_id: profile.id,
        ...formData
      });

      if (error) throw error;

      toast({
        title: t("requestSuccess"),
        description: t("requestSuccessDesc"),
      });

      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        goals: "",
      });
    } catch (error) {
      console.error('Error submitting request:', error);
      toast({
        title: t("requestError"),
        description: t("requestErrorDesc"),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button className="w-full sm:w-auto bg-[#33C3F0] hover:bg-[#1EAEE5] text-white shadow-md">
          {t("startTraining")}
        </Button>
      </DialogTrigger>
      <DialogContent className={`${isMobile ? 'w-[90%] max-w-[350px]' : 'sm:max-w-md'}`}>
        <DialogHeader>
          <DialogTitle>{t("registrationRequest")}</DialogTitle>
          <DialogDescription>
            {t("fillForm")} {profile.first_name}.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="first_name">{t("firstName")}</Label>
              <Input
                id="first_name"
                required
                value={formData.first_name}
                onChange={(e) => setFormData(prev => ({...prev, first_name: e.target.value}))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">{t("lastName")}</Label>
              <Input
                id="last_name"
                required
                value={formData.last_name}
                onChange={(e) => setFormData(prev => ({...prev, last_name: e.target.value}))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t("email")}</Label>
            <Input
              id="email"
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData(prev => ({...prev, email: e.target.value}))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">{t("phone")}</Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData(prev => ({...prev, phone: e.target.value}))}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="goals">{t("goals")}</Label>
            <Textarea
              id="goals"
              value={formData.goals}
              onChange={(e) => setFormData(prev => ({...prev, goals: e.target.value}))}
              placeholder={t("goalsPlaceholder")}
            />
          </div>
          <Button 
            type="submit" 
            className="w-full bg-[#33C3F0] hover:bg-[#1EAEE5] text-white"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("sending")}
              </>
            ) : (
              t("sendRequest")
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
